import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImmichImage } from '@/design-system/primitives/ImmichImage';
import { twx } from '@/utils/tailwind';

export interface PhotoLightboxItem {
  assetId: string;
  /** Optional caption shown bottom-left when present. */
  caption?: string;
}

export interface PhotoLightboxProps {
  photos: ReadonlyArray<PhotoLightboxItem>;
  /** Index into `photos` for the initial frame. Clamped to [0, photos.length-1]. */
  startIndex?: number;
  onClose: () => void;
  /** When provided, renders an "add more" button beside the close
   *  button. Use to keep the upload affordance reachable from the
   *  lightbox when the parent tile is a single-thumbnail preview. */
  onAddPhotos?: () => void;
  /** When provided, each frame gets an "×" button overlaying its
   *  top-left corner that calls this with the active assetId. Use
   *  for the unlink-from-place flow. */
  onRemovePhoto?: (assetId: string) => void;
}

/**
 * Fullscreen, portaled photo viewer. Loads the worker-proxied
 * `/api/immich/asset/:id/original` for each photo. Keyboard navigation:
 * Esc to close, ←/→ to step. Tap outside the image (the backdrop) also
 * closes — same convention as the trip-selector menu.
 */
export const PhotoLightbox = ({
  photos,
  startIndex = 0,
  onClose,
  onAddPhotos,
  onRemovePhoto,
}: PhotoLightboxProps) => {
  const count = photos.length;
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, startIndex), Math.max(0, count - 1)),
  );
  // When the photo set shrinks (e.g. user removes the active photo),
  // clamp the index so we don't render a stale slot. Closes when the
  // last photo is removed.
  useEffect(() => {
    if (count === 0) {
      onClose();
      return;
    }
    if (index >= count) setIndex(count - 1);
  }, [count, index, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (count <= 1) return;
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + count) % count);
      else if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, onClose]);

  // Lock body scroll while the lightbox is open so iOS doesn't let the
  // page underneath rubber-band away from the viewer.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (count === 0) return null;
  const active = photos[index];
  if (!active) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-modal bg-black/90 flex items-center justify-center motion-safe:animate-kawaii-modal-scrim"
      onClick={onClose}
    >
      {/* Image — stopPropagation so taps on the image itself don't
          close. Wrapped in a relative container so ImmichImage's
          absolute-positioned shimmer overlay has somewhere to anchor. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-[95vw] max-h-[90vh] flex items-center justify-center"
      >
        <ImmichImage
          key={active.assetId}
          // `preview` rather than `original`: browsers can't render raw
          // HEIC/HEIF bytes (except Safari), and the original endpoint
          // serves whatever was uploaded. `preview` is Immich's
          // server-transcoded JPEG and renders everywhere.
          assetId={active.assetId}
          size="preview"
          alt={active.caption ?? ''}
          className="max-w-[95vw] max-h-[90vh] object-contain motion-safe:animate-kawaii-modal-dialog"
        />
      </div>

      {/* Top-right action cluster — Add (optional), Delete (optional),
          then Close. */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {onAddPhotos && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddPhotos();
            }}
            aria-label="Add more photos"
            title="Add more photos"
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} style={{ width: 16, height: 16 }} />
          </button>
        )}
        {onRemovePhoto && (
          // Trash glyph rather than X so it's visually distinct from
          // the Close (X) button. Hover tint goes red for an extra
          // "destructive" cue.
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemovePhoto(active.assetId);
            }}
            aria-label="Remove this photo"
            title="Remove this photo"
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-error-main/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <FontAwesomeIcon icon={faTrashCan} style={{ width: 16, height: 16 }} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close photo viewer"
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
        >
          <FontAwesomeIcon icon={faXmark} style={{ width: 18, height: 18 }} />
        </button>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i - 1 + count) % count);
            }}
            aria-label="Previous photo"
            className={twx(
              'absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors',
            )}
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ width: 16, height: 16 }} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIndex((i) => (i + 1) % count);
            }}
            aria-label="Next photo"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <FontAwesomeIcon icon={faChevronRight} style={{ width: 16, height: 16 }} />
          </button>
          {/* Index pill bottom-center */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-medium"
          >
            {index + 1} / {count}
          </div>
        </>
      )}

      {active.caption && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-4 left-4 max-w-[60vw] px-3 py-1.5 rounded-lg bg-white/15 backdrop-blur-sm text-white text-xs truncate"
        >
          {active.caption}
        </div>
      )}
    </div>,
    document.body,
  );
};
